import React from "react";
import { Checkbox as AntCheckbox } from "antd";
import type { CheckboxProps as AntCheckboxProps, CheckboxRef } from "antd";
import type { CheckboxGroupProps as AntCheckboxGroupProps } from "antd/es/checkbox";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";

// ============================================================================
// Types
// ============================================================================

export type CheckboxSize = "small" | "medium" | "large";
export type CheckboxVariant = "default" | "filled" | "card";

export interface CheckboxProps extends Omit<AntCheckboxProps, "size"> {
  /** Size of the checkbox */
  size?: CheckboxSize;
  /** Visual variant */
  variant?: CheckboxVariant;
  /** Custom label for the checkbox */
  label?: React.ReactNode;
  /** Description text below the label */
  description?: string;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Additional className */
  className?: string;
  /** Wrapper className */
  wrapperClassName?: string;
}

export interface CheckboxGroupProps
  extends Omit<AntCheckboxGroupProps, "size"> {
  /** Size of checkboxes in the group */
  size?: CheckboxSize;
  /** Direction of the checkbox group */
  direction?: "horizontal" | "vertical";
  /** Gap between checkbox items */
  gap?: "small" | "medium" | "large";
  /** Additional className */
  className?: string;
}

export interface CheckboxOption {
  label: React.ReactNode;
  value: string | number | boolean;
  disabled?: boolean;
  description?: string;
}

export interface CheckboxCardProps
  extends Omit<AntCheckboxProps, "size" | "title" | "description"> {
  /** Card title */
  title: React.ReactNode;
  /** Card description */
  description?: React.ReactNode;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Size variant */
  size?: CheckboxSize;
  /** Additional className */
  className?: string;
}

export interface CheckboxWithBorderProps extends CheckboxProps {
  /** Whether the checkbox is selected */
  selected?: boolean;
}

// ============================================================================
// Size Configurations
// ============================================================================

const sizeConfig = {
  small: {
    checkbox: 16,
    checkmark: 10,
    fontSize: "text-xs",
    fontSizeValue: 12,
    padding: "py-1",
    gap: "gap-1.5",
    borderRadius: 2,
  },
  medium: {
    checkbox: 20,
    checkmark: 12,
    fontSize: "text-sm",
    fontSizeValue: 14,
    padding: "py-1.5",
    gap: "gap-2",
    borderRadius: 4,
  },
  large: {
    checkbox: 24,
    checkmark: 14,
    fontSize: "text-base",
    fontSizeValue: 16,
    padding: "py-2",
    gap: "gap-2.5",
    borderRadius: 4,
  },
};

const gapConfig = {
  small: "gap-2",
  medium: "gap-4",
  large: "gap-6",
};

// ============================================================================
// CSS Styles
// ============================================================================

const getCheckboxStyles = (
  size: CheckboxSize,
  variant: CheckboxVariant = "default"
) => {
  const config = sizeConfig[size];
  const checkedBg = variant === "filled" ? colors.primary : colors.white;
  const checkmarkColor = variant === "filled" ? colors.white : colors.primary;

  // Calculate checkmark dimensions based on checkbox size
  const checkmarkWidth = Math.round(config.checkbox * 0.3);
  const checkmarkHeight = Math.round(config.checkbox * 0.55);

  return `
    .ds-checkbox-${size}-${variant} .ant-checkbox-inner {
      width: ${config.checkbox}px !important;
      height: ${config.checkbox}px !important;
      border-radius: ${config.borderRadius}px !important;
      border: 1.5px solid ${colors.surfaceDark} !important;
      background-color: ${colors.white} !important;
      transition: all 0.2s ease-in-out !important;
    }
    .ds-checkbox-${size}-${variant} .ant-checkbox-checked .ant-checkbox-inner {
      background-color: ${checkedBg} !important;
      border-color: ${colors.primary} !important;
    }
    .ds-checkbox-${size}-${variant} .ant-checkbox-checked .ant-checkbox-inner::after {
      width: ${checkmarkWidth}px !important;
      height: ${checkmarkHeight}px !important;
      border-color: ${checkmarkColor} !important;
      border-width: 2px !important;
      border-top: 0 !important;
      border-left: 0 !important;
      left: 50% !important;
      top: 45% !important;
      transform: rotate(45deg) translate(-50%, -50%) !important;
      transform-origin: 0% 0% !important;
    }
    .ds-checkbox-${size}-${variant} .ant-checkbox:hover .ant-checkbox-inner {
      border-color: ${colors.primary} !important;
    }
    .ds-checkbox-${size}-${variant} .ant-checkbox-disabled .ant-checkbox-inner {
      background-color: ${colors.surfaceLighter} !important;
      border-color: ${colors.surfaceSubtle} !important;
    }
    .ds-checkbox-${size}-${variant} .ant-checkbox-disabled .ant-checkbox-inner::after {
      border-color: ${colors.surfaceDark} !important;
    }
    .ds-checkbox-${size}-${variant} .ant-checkbox-wrapper {
      color: ${colors.textDefault};
      align-items: flex-start;
      font-size: ${config.fontSizeValue}px;
    }
    .ds-checkbox-${size}-${variant} .ant-checkbox-wrapper-disabled {
      color: ${colors.textMuted};
    }
    .ds-checkbox-${size}-${variant} .ant-checkbox-indeterminate .ant-checkbox-inner::after {
      background-color: ${colors.primary} !important;
      width: ${Math.floor(config.checkbox * 0.5)}px !important;
      height: 2px !important;
      border: none !important;
      transform: translate(-50%, -50%) !important;
      left: 50% !important;
      top: 50% !important;
    }
    .ds-checkbox-${size}-${variant}-error .ant-checkbox-inner {
      border-color: ${colors.error} !important;
    }
    .ds-checkbox-${size}-${variant}-error .ant-checkbox:hover .ant-checkbox-inner {
      border-color: ${colors.errorHover} !important;
    }
  `;
};

// ============================================================================
// Checkbox Component
// ============================================================================

export const Checkbox = React.forwardRef<CheckboxRef, CheckboxProps>(
  (
    {
      size = "medium",
      variant = "filled",
      label,
      description,
      error,
      errorMessage,
      className,
      wrapperClassName,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const config = sizeConfig[size];
    const checkboxClass = `ds-checkbox-${size}-${variant}`;
    const errorClass = error ? `ds-checkbox-${size}-${variant}-error` : "";

    const content =
      label || children ? (
        <span className={cn("flex flex-col", config.gap)}>
          <span
            className={cn(config.fontSize, "font-medium leading-tight")}
            style={{ color: disabled ? colors.textMuted : colors.textDarkest }}
          >
            {label || children}
          </span>
          {description && (
            <span
              className="text-xs leading-relaxed"
              style={{ color: colors.textMuted }}
            >
              {description}
            </span>
          )}
        </span>
      ) : undefined;

    return (
      <div className={wrapperClassName}>
        <style>{getCheckboxStyles(size, variant)}</style>
        <AntCheckbox
          ref={ref}
          className={cn(checkboxClass, errorClass, className)}
          disabled={disabled}
          {...props}
        >
          {content}
        </AntCheckbox>
        {error && errorMessage && (
          <p className="text-xs mt-1 ml-6" style={{ color: colors.error }}>
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

// ============================================================================
// Checkbox Group Component
// ============================================================================

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  size = "medium",
  direction = "horizontal",
  gap = "medium",
  className,
  children,
  options,
  ...props
}) => {
  const directionClass =
    direction === "vertical" ? "flex-col" : "flex-row flex-wrap";

  // If options are provided, render them
  const renderOptions = () => {
    if (!options) return children;

    return options.map((option) => {
      if (typeof option === "string" || typeof option === "number") {
        return (
          <Checkbox key={String(option)} value={option} size={size}>
            {option}
          </Checkbox>
        );
      }

      const opt = option as CheckboxOption;
      return (
        <Checkbox
          key={String(opt.value)}
          value={opt.value}
          disabled={opt.disabled}
          size={size}
          label={opt.label}
          description={opt.description}
        />
      );
    });
  };

  return (
    <>
      <style>{getCheckboxStyles(size)}</style>
      <AntCheckbox.Group
        className={cn(
          `ds-checkbox-${size}-default`,
          "flex",
          directionClass,
          gapConfig[gap],
          className
        )}
        {...props}
      >
        {renderOptions()}
      </AntCheckbox.Group>
    </>
  );
};

// ============================================================================
// Checkbox Card Component (Card-style selection)
// ============================================================================

export const CheckboxCard: React.FC<CheckboxCardProps> = ({
  title,
  description,
  icon,
  size = "medium",
  className,
  checked,
  disabled,
  ...props
}) => {
  const config = sizeConfig[size];

  return (
    <>
      <style>{getCheckboxStyles(size, "filled")}</style>
      <label
        className={cn(
          "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200",
          disabled && "cursor-not-allowed opacity-60",
          className
        )}
        style={{
          borderColor:
            checked && !disabled ? colors.primary : colors.surfaceSubtle,
          backgroundColor:
            checked && !disabled
              ? colors.primaryExtraLight
              : colors.surfaceLightest,
        }}
      >
        <AntCheckbox
          checked={checked}
          disabled={disabled}
          className={`ds-checkbox-${size}-filled`}
          {...props}
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {icon && (
              <span
                style={{ color: checked ? colors.primary : colors.textMuted }}
              >
                {icon}
              </span>
            )}
            <span
              className={cn(config.fontSize, "font-semibold")}
              style={{
                color: disabled ? colors.textMuted : colors.textDarkest,
              }}
            >
              {title}
            </span>
          </div>
          {description && (
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
              {description}
            </p>
          )}
        </div>
      </label>
    </>
  );
};

// ============================================================================
// Checkbox with Border Component
// ============================================================================

export const CheckboxWithBorder: React.FC<CheckboxWithBorderProps> = ({
  size = "medium",
  label,
  description,
  className,
  checked,
  disabled,
  children,
  ...props
}) => {
  const config = sizeConfig[size];
  const isSelected = checked;

  return (
    <>
      <style>{getCheckboxStyles(size, "filled")}</style>
      <label
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200",
          disabled && "cursor-not-allowed opacity-60",
          className
        )}
        style={{
          borderColor:
            isSelected && !disabled ? colors.primary : colors.surfaceSubtle,
          backgroundColor:
            isSelected && !disabled ? colors.primaryExtraLight : colors.white,
        }}
      >
        <AntCheckbox
          checked={checked}
          disabled={disabled}
          className={`ds-checkbox-${size}-filled`}
          {...props}
        />
        <span className="flex flex-col flex-1">
          <span
            className={cn(config.fontSize, "font-medium")}
            style={{ color: disabled ? colors.textMuted : colors.textDefault }}
          >
            {label || children}
          </span>
          {description && (
            <span
              className="text-xs mt-0.5"
              style={{ color: colors.textMuted }}
            >
              {description}
            </span>
          )}
        </span>
      </label>
    </>
  );
};

// ============================================================================
// Checkbox with Info Box Component (like the invite modal)
// ============================================================================

export interface CheckboxInfoBoxProps extends Omit<AntCheckboxProps, "size"> {
  /** Size of the checkbox */
  size?: CheckboxSize;
  /** Main label text */
  label: React.ReactNode;
  /** Description text */
  description?: React.ReactNode;
  /** Additional action element (like a link) */
  action?: React.ReactNode;
  /** Background color variant */
  bgVariant?: "info" | "success" | "warning" | "neutral";
  /** Additional className */
  className?: string;
}

const bgVariantConfig = {
  info: {
    bg: colors.infoBgSubtle,
    border: colors.infoBorder,
  },
  success: {
    bg: colors.successBgSubtle,
    border: colors.successBorder,
  },
  warning: {
    bg: colors.warningBgSubtle,
    border: colors.warningBorder,
  },
  neutral: {
    bg: colors.surfaceLighter,
    border: colors.surfaceSubtle,
  },
};

export const CheckboxInfoBox: React.FC<CheckboxInfoBoxProps> = ({
  size = "medium",
  label,
  description,
  action,
  bgVariant = "info",
  className,
  disabled,
  ...props
}) => {
  const config = sizeConfig[size];
  const bgConfig = bgVariantConfig[bgVariant];

  return (
    <>
      <style>{getCheckboxStyles(size, "filled")}</style>
      <div
        className={cn("rounded-lg p-4", className)}
        style={{
          backgroundColor: bgConfig.bg,
          border: `1px solid ${bgConfig.border}`,
        }}
      >
        <div className="flex items-start gap-3">
          <AntCheckbox
            disabled={disabled}
            className={`ds-checkbox-${size}-filled`}
            style={{ marginTop: 2 }}
            {...props}
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={cn(config.fontSize, "font-medium")}
                style={{
                  color: disabled ? colors.textMuted : colors.textDarkest,
                }}
              >
                {label}
              </span>
              {action}
            </div>
            {description && (
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                {description}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// Default Export
// ============================================================================

export default Checkbox;
